import { useNavigate } from '@tanstack/react-router';
import { Button, toast } from '@devhop/ui';
import { ArrowLeft, HelpCircle } from 'lucide-react';
import { Header } from '@/app/layout/Header';
import { Main } from '@/app/layout/Main';
import type { CreatePromotionPathRuleDTO } from '@/modules/hr/promotion/domain/types';
import { useCreatePromotionRuleMutation } from '../api/queries';
import { PromotionRuleForm } from '../ui/PromotionRuleForm';
import {
    PromotionTour,
    promotionRuleCreateTourSteps,
    usePromotionTour,
} from '../tour';

export function PromotionRuleCreatePage() {
    const nav = useNavigate();
    const createMutation = useCreatePromotionRuleMutation();
    const tour = usePromotionTour('ruleCreate');

    const back = () =>
        nav({
            to: '/app/hr/promotion/rules',
            search: {
                offset: 0,
                limit: 20,
                sort: undefined,
                isActive: undefined,
                fromPositionId: undefined,
                toPositionId: undefined,
                search: undefined,
            },
        });

    const submit = (values: CreatePromotionPathRuleDTO) => {
        createMutation.mutate(values, {
            onSuccess: () => {
                toast.success('ສ້າງ rule ສຳເລັດ');
                back();
            },
            onError: (err) =>
                toast.error(err.message ?? 'ບໍ່ສາມາດສ້າງ rule ໄດ້'),
        });
    };

    return (
        <>
            <Header />
            <Main>
                <PromotionTour
                    steps={promotionRuleCreateTourSteps}
                    run={tour.run}
                    onCallback={tour.handleJoyrideCallback}
                />
                <div
                    className="mb-6 flex items-center justify-between gap-2"
                    data-tourid="promotion-rule-create-back"
                >
                    <div>
                        <div className="flex items-center gap-2">
                            <h1 className="font-semibold text-2xl">
                                ສ້າງຂໍ້ກຳນົດການເລື່ອນຕຳແໜ່ງ
                            </h1>
                            <Button
                                variant="ghost"
                                size="icon"
                                className="size-7 rounded-full"
                                onClick={tour.startTour}
                                title="ເລີ່ມທົດສອບການນຳທາງ"
                                aria-label="ເລີ່ມທົດສອບການນຳທາງ"
                            >
                                <HelpCircle className="size-5" />
                            </Button>
                        </div>
                        <p className="text-muted-foreground text-sm">
                            ກຳນົດເສັ້ນທາງການເລື່ອນຕຳແໜ່ງ
                            ຈາກຕຳແໜ່ງໜຶ່ງໄປອີກຕຳແໜ່ງໜຶ່ງ
                        </p>
                    </div>
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={back}
                        className="gap-2"
                    >
                        <ArrowLeft className="h-4 w-4" />
                        ກັບໄປລາຍການ
                    </Button>
                </div>
                <div
                    className="mx-auto max-w-3xl"
                    data-tourid="promotion-rule-create-form"
                >
                    <PromotionRuleForm
                    mode="create"
                    onSubmit={submit}
                    onCancel={back}
                    isSubmitting={createMutation.isPending}
                    />
                </div>
            </Main>
        </>
    );
}
